import { useShellAuth0 } from "@opengov/shell-auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ServicePath } from "../../constants/apiConstants";
import { QUERY_CACHE_PERIOD } from "../../constants/queryConstants";
import { getFetchQuery } from "../core/rest/useFetchQuery";
import type { NotificationsApiResponse, FetchNotificationsParams } from "./notifications.types";
import { notificationsKeys } from "./notifications.queryKeys";

interface UseNotificationsQueryOptions {
  params?: FetchNotificationsParams;
}

export function useNotificationsQuery(baseUrl: string, entityId: string, options?: UseNotificationsQueryOptions) {
  const params = options?.params;
  const { getAccessTokenSilently } = useShellAuth0();

  const buildUrl = (pageParam?: string) => {
    const query = new URLSearchParams({ entityId });
    if (params?.cursor) query.append("cursor", params.cursor);
    if (params?.read !== undefined) query.append("read", params.read);
    if (params?.starred !== undefined) query.append("starred", params.starred);
    if (params?.taskId) query.append("taskId", params.taskId);
    if (pageParam) query.set("cursor", pageParam);
    return `${baseUrl}${ServicePath.GlobalNavNotificationsServiceGraphql}?${query.toString()}`;
  };

  return useInfiniteQuery<NotificationsApiResponse, Error>({
    queryKey: notificationsKeys.infiniteList(entityId, params ?? {}),
    queryFn: async (context) => {
      const fetchQueryOptions = getFetchQuery<NotificationsApiResponse>({
        queryKey: notificationsKeys.infiniteList(entityId, params ?? {}),
        url: buildUrl(context.pageParam as string | undefined),
        getAccessToken: getAccessTokenSilently,
      });
      if (!fetchQueryOptions.queryFn) {
        throw new Error("queryFn was not returned by getFetchQuery");
      }
      return fetchQueryOptions.queryFn(context);
    },
    getNextPageParam: (lastPage: NotificationsApiResponse) => lastPage.nextCursor,
    staleTime: QUERY_CACHE_PERIOD.SHORT,
    // Mirror useUnreadCount's polling cadence so the drawer list and the bell
    // badge stay in sync without consumers needing to coordinate the two caches.
    refetchInterval: QUERY_CACHE_PERIOD.VERY_SHORT,
  });
}
